/**
 * Nominatim (OpenStreetMap) geocoding, restricted to Romania.
 *
 * Used only to move the map to a locality before drawing, and to guess the
 * locality/county from a drawn parcel's centroid. It never confirms boundaries.
 *
 * Usage policy compliance — https://operations.osmfoundation.org/policies/nominatim/
 *  - Absolute maximum of 1 request/second. `throttle()` below is a hard serial
 *    gate at module scope, so every caller shares one queue. The UI debounce is
 *    a separate, softer guard; this is the one that keeps us within policy even
 *    if a component misbehaves.
 *  - The policy asks for an identifying User-Agent. Browsers forbid setting that
 *    header, so we identify via the `email` parameter (which Nominatim documents
 *    for exactly this purpose) and the Referer the browser sends automatically.
 *  - Results are cached per query for the page session to avoid repeat calls.
 */

const BASE_URL = (import.meta.env.VITE_NOMINATIM_URL || 'https://nominatim.openstreetmap.org').replace(
  /\/$/,
  '',
);
const CONTACT_EMAIL = import.meta.env.VITE_NOMINATIM_EMAIL || '';

const MIN_INTERVAL_MS = 1100; // 1 req/sec, plus headroom for clock skew.

let lastRequestAt = 0;
let chain: Promise<unknown> = Promise.resolve();

/** Serialises all callers and spaces them at least MIN_INTERVAL_MS apart. */
function throttle<T>(task: () => Promise<T>): Promise<T> {
  const run = chain.then(async () => {
    const wait = Math.max(0, lastRequestAt + MIN_INTERVAL_MS - Date.now());
    if (wait > 0) await new Promise((resolve) => setTimeout(resolve, wait));
    lastRequestAt = Date.now();
    return task();
  });
  // Keep the chain alive even if this task rejects, or one failure stalls the queue.
  chain = run.catch(() => undefined);
  return run;
}

export interface GeocodeResult {
  id: string;
  label: string;
  lat: number;
  lon: number;
  /** Viewport [south, north, west, east] when Nominatim supplies one. */
  boundingBox: [number, number, number, number] | null;
}

export interface ReverseResult {
  locality: string;
  judetRaw: string;
}

interface NominatimAddress {
  city?: string;
  town?: string;
  village?: string;
  municipality?: string;
  suburb?: string;
  neighbourhood?: string;
  city_district?: string;
  county?: string;
  state?: string;
}

interface NominatimPlace {
  place_id: number;
  display_name: string;
  lat: string;
  lon: string;
  boundingbox?: [string, string, string, string];
  address?: NominatimAddress;
}

function buildUrl(path: string, params: Record<string, string>): string {
  const url = new URL(`${BASE_URL}${path}`);
  url.searchParams.set('format', 'jsonv2');
  url.searchParams.set('countrycodes', 'ro');
  url.searchParams.set('accept-language', 'ro');
  for (const [key, value] of Object.entries(params)) url.searchParams.set(key, value);
  if (CONTACT_EMAIL) url.searchParams.set('email', CONTACT_EMAIL);
  return url.toString();
}

const searchCache = new Map<string, GeocodeResult[]>();

/**
 * Forward-geocode a locality or address within Romania.
 * Rejects with the AbortError if `signal` fires, so callers can drop stale queries.
 */
export async function searchPlaces(query: string, signal?: AbortSignal): Promise<GeocodeResult[]> {
  const trimmed = query.trim();
  if (trimmed.length < 3) return [];

  const cacheKey = trimmed.toLowerCase();
  const cached = searchCache.get(cacheKey);
  if (cached) return cached;

  let results: GeocodeResult[];
  try {
    results = await searchPhoton(trimmed, signal);
    // Photon knows the place names but not always the administrative ones; an empty
    // result is not a failure, so fall through to Nominatim rather than show nothing.
    if (results.length === 0) results = await searchNominatim(trimmed, signal);
  } catch (photonErr) {
    if (isAbort(photonErr)) throw photonErr; // a superseded query, not an outage
    results = await searchNominatim(trimmed, signal);
  }

  searchCache.set(cacheKey, results);
  return results;
}

/**
 * Photon (komoot) — a geocoder over the same OpenStreetMap data, but with FUZZY
 * matching: it tolerates the typos Nominatim rejects outright. "Ghimpai", "Otopenii",
 * "Dulcanca" all resolve here and all fail there.
 *
 * That matters more than convenience. An address the owner cannot get to match is an
 * address typed freehand, and freehand locations are what leave a parcel without a
 * resolved locality — the rung where the price grid is trustworthy. Photon leads,
 * Nominatim covers what it misses and stands in when it is down.
 *
 * The box is ROMANIA, not a city: this form takes parcels from all 42 counties.
 */
const PHOTON_URL = 'https://photon.komoot.io/api';
const PHOTON_BBOX = '20.26,43.62,29.71,48.27';

interface PhotonFeature {
  properties: {
    osm_id?: number;
    osm_type?: string;
    name?: string;
    street?: string;
    housenumber?: string;
    district?: string;
    city?: string;
    county?: string;
    state?: string;
    extent?: [number, number, number, number];
  };
  geometry: { coordinates: [number, number] };
}

function photonToResult(f: PhotonFeature, index: number): GeocodeResult | null {
  const p = f.properties;
  const primary = p.name ?? (p.street ? `${p.street}${p.housenumber ? ` ${p.housenumber}` : ''}` : null);
  if (!primary) return null;
  // County last, so the label reads the way the reverse-geocode writes it and an owner
  // can tell two same-named communes apart.
  const rest = [p.district, p.city, p.county ?? p.state].filter((x): x is string => Boolean(x) && x !== primary);
  const [lon, lat] = f.geometry.coordinates;
  // Photon's extent is [minLon, maxLat, maxLon, minLat]; the map wants [S, N, W, E].
  const e = p.extent;
  return {
    id: p.osm_id != null ? `${p.osm_type ?? ''}${p.osm_id}` : `photon-${index}`,
    label: [primary, ...rest].join(', '),
    lat,
    lon,
    boundingBox: e ? [e[3], e[1], e[0], e[2]] : null,
  };
}

async function searchPhoton(query: string, signal?: AbortSignal): Promise<GeocodeResult[]> {
  const url = new URL(PHOTON_URL);
  url.searchParams.set('q', query);
  url.searchParams.set('bbox', PHOTON_BBOX);
  url.searchParams.set('lang', 'default');
  url.searchParams.set('limit', '10');
  return throttle(async () => {
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
    const response = await fetch(url.toString(), { signal: signal ?? null, headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(`Photon search failed: ${response.status}`);
    const data = (await response.json()) as { features?: PhotonFeature[] };
    // Photon often returns several segments of the same street: dedupe on the label.
    const seen = new Set<string>();
    const out: GeocodeResult[] = [];
    for (const [i, f] of (data.features ?? []).entries()) {
      const r = photonToResult(f, i);
      if (!r || seen.has(r.label)) continue;
      seen.add(r.label);
      out.push(r);
      if (out.length >= 6) break;
    }
    return out;
  });
}

async function searchNominatim(query: string, signal?: AbortSignal): Promise<GeocodeResult[]> {
  const url = buildUrl('/search', { q: query, limit: '6', addressdetails: '1' });
  return throttle(async () => {
    // Re-check after the queue wait: the user may have typed on since we queued.
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
    const response = await fetch(url, { signal: signal ?? null, headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(`Nominatim search failed: ${response.status}`);
    const places = (await response.json()) as NominatimPlace[];
    return places.map(toGeocodeResult);
  });
}

const isAbort = (err: unknown): boolean => err instanceof DOMException && err.name === 'AbortError';

function toGeocodeResult(place: NominatimPlace): GeocodeResult {
  const bb = place.boundingbox;
  return {
    id: String(place.place_id),
    label: place.display_name,
    lat: Number.parseFloat(place.lat),
    lon: Number.parseFloat(place.lon),
    boundingBox: bb
      ? [Number.parseFloat(bb[0]), Number.parseFloat(bb[1]), Number.parseFloat(bb[2]), Number.parseFloat(bb[3])]
      : null,
  };
}

/**
 * Reverse-geocode a parcel centroid to prefill locality and county.
 * Returns null rather than throwing: a failed prefill is a non-event, the owner
 * can always type the locality themselves.
 */
export async function reverseGeocode(lat: number, lon: number): Promise<ReverseResult | null> {
  const url = buildUrl('/reverse', {
    lat: String(lat),
    lon: String(lon),
    zoom: '14',
    addressdetails: '1',
  });

  try {
    return await throttle(async () => {
      const response = await fetch(url, { headers: { Accept: 'application/json' } });
      if (!response.ok) return null;
      const place = (await response.json()) as NominatimPlace;
      const a = place.address;
      if (!a) return null;

      const locality =
        a.city ??
        a.town ??
        a.village ??
        a.municipality ??
        a.suburb ??
        a.neighbourhood ??
        a.city_district ??
        '';

      return { locality, judetRaw: a.county ?? a.state ?? '' };
    });
  } catch {
    return null;
  }
}

/** Shorten Nominatim's long display_name to something readable in a result list. */
export function shortLabel(label: string): string {
  const parts = label.split(',').map((p) => p.trim()).filter(Boolean);
  if (parts.length <= 3) return parts.join(', ');
  // Keep the most specific two and the county; drop postcode/country tail.
  return [parts[0], parts[1], parts[parts.length - 3]].filter(Boolean).join(', ');
}
